import { ChangeDetectionStrategy, ChangeDetectorRef, Component, forwardRef, OnInit, Provider } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { PaymentOptionsBaseComponent } from '@luggagehero/features/bookings';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { Config } from '@luggagehero/shared/environment';
import { SharedBookingService } from '@luggagehero/shared/services/bookings';
import { SharedErrorService } from '@luggagehero/shared/services/error';
import { SharedLoggingService } from '@luggagehero/shared/services/logging';
import { SharedNotificationService } from '@luggagehero/shared/services/notification';
import { SharedOrderService } from '@luggagehero/shared/services/orders';
import { SharedPaymentService } from '@luggagehero/shared/services/payments';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { SharedStripeService } from '@luggagehero/shared/services/stripe';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { SharedUtilString } from '@luggagehero/shared/util';
import { TranslateService } from '@ngx-translate/core';
import { PaymentMethod, PaymentRequest, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';

export const PAYMENT_OPTIONS_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => PaymentOptionsComponent),
  multi: true,
};

@Component({
  selector: 'lh-payment-options',
  templateUrl: './payment-options.component.html',
  styleUrls: ['./payment-options.component.scss'],
  providers: [PAYMENT_OPTIONS_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentOptionsComponent extends PaymentOptionsBaseComponent implements OnInit {
  private browserPaymentRequest: PaymentRequest;

  constructor(
    cd: ChangeDetectorRef,
    bookingService: SharedBookingService,
    orderService: SharedOrderService,
    paymentService: SharedPaymentService,
    private stripe: SharedStripeService,
    notify: SharedNotificationService,
    error: SharedErrorService,
    translate: TranslateService,
    private log: SharedLoggingService,
    windowService: SharedWindowService,
    storageService: SharedStorageService,
  ) {
    super(cd, bookingService, orderService, paymentService, notify, error, translate, windowService, storageService);
  }

  public async ngOnInit(): Promise<void> {
    this.isApplePayAvailable = this.stripe.isApplePayAvailable;
    this.isGooglePayAvailable = this.stripe.isGooglePayAvailable;
    this.isBrowserPaymentAvailable = this.stripe.isWalletPaymentAvailable;

    void this.log.info(`Browser payment available: ${this.isBrowserPaymentAvailable}`, {
      isApplePayAvailable: this.isApplePayAvailable,
      isGooglePayAvailable: this.isGooglePayAvailable,
    });

    const label = this.translate.instant(AppConfig.PAYMENT_VERIFICATION_LABEL) as string;

    const amount = 0;
    const currency = this.paymentCurrency;

    // The two-letter country code of your Stripe account (e.g., US).
    const country = AppConfig.MERCHANT_COUNTRY_CODE;

    this.browserPaymentRequest = await this.stripe.requestBrowserPayment(amount, currency, country, label);

    if (this.browserPaymentRequest) {
      this.browserPaymentRequest.on('paymentmethod', (e) => this.onPaymentMethodEvent(e));
    }

    await super.ngOnInit();
  }

  showBrowserPayment() {
    if (!this.isBrowserPaymentAvailable) {
      return;
    }
    this.browserPaymentRequest.update({
      total: {
        amount: this.paymentAmount,
        label: this.translate.instant(AppConfig.PAYMENT_VERIFICATION_LABEL) as string,
      },
      currency: this.paymentCurrency,
    });
    this.browserPaymentRequest.show();
  }

  async onPaymentMethodEvent(e: PaymentRequestPaymentMethodEvent): Promise<void> {
    let paymentMethod: PaymentMethod;

    try {
      paymentMethod = await this.stripe.confirmBrowserPayment(this.payment.data.client_secret, e);
    } catch (err) {
      this.notify.error((err as { message: string }).message);

      if (!Config.isProduction) {
        console.debug(SharedUtilString.formatPaymentEvent(this.payment.data, 'failed'), err);
      }
    }

    if (paymentMethod) {
      if (!Config.isProduction) {
        console.debug(SharedUtilString.formatPaymentEvent(this.payment.data, 'confirmed'));
      }

      this.walletPaymentMethodAdded = await this.addPaymentMethod(paymentMethod);
    }
  }
}
